import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Plus, CheckCircle2, FileEdit, Clock, Trash2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { supabase } from '@/lib/supabase/client'
import { useProjectRole } from '@/features/auth/context/project-role-context'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { useToast } from '@/hooks/use-toast'
import { discardDraft, RecordLockedError } from '../api/records-api'
import { cn } from '@/lib/utils/cn'
import dayjs from 'dayjs'

interface InstanceRow {
  id: string
  instance_index: number
  status: 'draft' | 'submitted'
  field_values: Record<string, unknown>
  updated_at: string
  submitted_at: string | null
}

interface FormInfo {
  id: string
  title: unknown
  instance_label_field_key: string | null
  minimum_instances: number | null
  instance_type: string
  required_for_final_submit: boolean
}

async function fetchInstances(recordId: string, formId: string) {
  const [formRes, subsRes, recordRes] = await Promise.all([
    supabase
      .from('forms')
      .select('id, title, instance_label_field_key, minimum_instances, instance_type, required_for_final_submit')
      .eq('id', formId)
      .single(),
    supabase
      .from('submissions')
      .select('id, instance_index, status, field_values, updated_at, submitted_at')
      .eq('record_id', recordId)
      .eq('form_id', formId)
      .eq('is_deleted', false)
      .order('instance_index', { ascending: true }),
    supabase.from('records').select('display_id').eq('id', recordId).single(),
  ])

  if (formRes.error) throw formRes.error

  return {
    form: formRes.data as FormInfo,
    instances: (subsRes.data ?? []) as InstanceRow[],
    recordDisplayId: recordRes.data?.display_id ?? recordId,
  }
}

function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

export function FormInstancesPage() {
  const { projectId, recordId, formId } = useParams<{
    projectId: string
    recordId: string
    formId: string
  }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { toast } = useToast()
  const { canEdit } = useProjectRole()
  const user = useAuthStore((s) => s.user)

  const { data, isLoading } = useQuery({
    queryKey: ['form-instances', recordId, formId],
    queryFn: () => fetchInstances(recordId!, formId!),
    enabled: !!recordId && !!formId,
  })

  const [discardTarget, setDiscardTarget] = useState<{ id: string; label: string } | null>(null)

  const discardMutation = useMutation({
    mutationFn: (submissionId: string) => discardDraft(submissionId, user!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form-instances', recordId, formId] })
      qc.invalidateQueries({ queryKey: ['record-forms'] })
      // A discarded draft can be the submission feeding a summary value.
      qc.invalidateQueries({ queryKey: ['summary-card', projectId, recordId] })
      toast({ title: 'پیش‌نویس حذف شد', duration: 2000 })
      setDiscardTarget(null)
    },
    onError: (err: Error) => {
      const description = err instanceof RecordLockedError
        ? 'سابقه در حالت بررسی یا تأییدشده است؛ نمی‌توان پیش‌نویس را حذف کرد.'
        : err.message
      toast({ title: 'خطا در حذف پیش‌نویس', description, variant: 'destructive' })
      setDiscardTarget(null)
    },
  })

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 bg-muted rounded animate-pulse w-64" />
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (!data) return null

  const { form, instances, recordDisplayId } = data
  const formTitle = parseTitle(form.title)
  const minInstances = form.minimum_instances ?? 1
  const submittedCount = instances.filter((i) => i.status === 'submitted').length
  const progressMet = submittedCount >= minInstances

  function getInstanceLabel(inst: InstanceRow): string {
    if (form.instance_label_field_key) {
      const val = inst.field_values?.[form.instance_label_field_key]
      if (val !== null && val !== undefined && val !== '') {
        return String(val)
      }
    }
    return `نمونه ${inst.instance_index.toLocaleString('fa-IR')}`
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 shrink-0">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
          <Link to={`/projects/${projectId}/records`} className="hover:text-foreground transition-colors">
            سوابق
          </Link>
          <ArrowRight className="w-3 h-3 rotate-180" />
          <Link
            to={`/projects/${projectId}/records/${recordId}`}
            className="hover:text-foreground transition-colors"
          >
            {recordDisplayId}
          </Link>
          <ArrowRight className="w-3 h-3 rotate-180" />
          <span className="text-foreground">{formTitle}</span>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base font-bold text-foreground">{formTitle}</h1>
              {form.required_for_final_submit ? (
                <span className="text-[10px] text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">اجباری</span>
              ) : (
                <span className="text-[10px] text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">اختیاری</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {instances.length.toLocaleString('fa-IR')} نمونه ·{' '}
              {submittedCount.toLocaleString('fa-IR')} ارسال شده
              {minInstances > 1 && (
                <span className={cn('ms-1', progressMet ? 'text-emerald-400' : 'text-yellow-400')}>
                  (حداقل {minInstances.toLocaleString('fa-IR')} مورد لازم)
                </span>
              )}
            </p>
          </div>
          {canEdit && (
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() =>
                navigate(`/projects/${projectId}/records/${recordId}/forms/${formId}/new`)
              }
            >
              <Plus className="w-4 h-4" />
              نمونه جدید
            </Button>
          )}
        </div>
      </div>

      {/* Instances list */}
      <div className="flex-1 overflow-y-auto p-6">
        {instances.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
              <FileEdit className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">نمونه‌ای ثبت نشده</p>
            <p className="text-xs text-muted-foreground mb-4">
              اولین نمونه را با دکمه بالا ثبت کنید
            </p>
            {canEdit && (
              <Button
                size="sm"
                onClick={() =>
                  navigate(`/projects/${projectId}/records/${recordId}/forms/${formId}/new`)
                }
              >
                <Plus className="w-4 h-4 me-1" />
                افزودن نمونه
              </Button>
            )}
          </div>
        ) : (
          <div className="max-w-2xl mx-auto space-y-2">
            {instances.map((inst) => (
              <div
                key={inst.id}
                className="w-full bg-card border border-border rounded-xl px-5 py-4 flex items-center gap-4 hover:bg-muted/20 transition-colors text-start cursor-pointer"
                onClick={() =>
                  navigate(
                    `/projects/${projectId}/records/${recordId}/forms/${formId}?submissionId=${inst.id}`
                  )
                }
              >
                {/* Status icon */}
                <div
                  className={cn(
                    'w-9 h-9 rounded-full flex items-center justify-center shrink-0',
                    inst.status === 'submitted'
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : 'bg-yellow-500/15 text-yellow-400'
                  )}
                >
                  {inst.status === 'submitted' ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Clock className="w-4 h-4" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{getInstanceLabel(inst)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {inst.status === 'submitted'
                      ? `ارسال شده · ${dayjs(inst.submitted_at).format('YYYY/MM/DD')}`
                      : `پیش‌نویس · آخرین ویرایش ${dayjs(inst.updated_at).fromNow()}`}
                  </p>
                </div>

                {/* Status badge */}
                <span
                  className={cn(
                    'text-xs px-2.5 py-1 rounded-full shrink-0',
                    inst.status === 'submitted'
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-yellow-500/10 text-yellow-400'
                  )}
                >
                  {inst.status === 'submitted' ? 'ارسال شده' : 'پیش‌نویس'}
                </span>

                {canEdit && inst.status === 'draft' && (
                  <button
                    type="button"
                    title="حذف پیش‌نویس"
                    aria-label="حذف پیش‌نویس"
                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0 disabled:opacity-50"
                    disabled={discardMutation.isPending}
                    onClick={(e) => {
                      e.stopPropagation()
                      setDiscardTarget({ id: inst.id, label: getInstanceLabel(inst) })
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog
        open={!!discardTarget}
        onOpenChange={(o) => { if (!o) setDiscardTarget(null) }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              حذف پیش‌نویس
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {discardTarget?.label
              ? `«${discardTarget.label}» را به‌طور دائم حذف می‌کنید؟ این کار غیرقابل بازگشت است.`
              : 'این پیش‌نویس را حذف می‌کنید؟ این کار غیرقابل بازگشت است.'}
          </p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDiscardTarget(null)}>
              انصراف
            </Button>
            <Button
              variant="destructive"
              disabled={discardMutation.isPending}
              onClick={() => discardTarget && discardMutation.mutate(discardTarget.id)}
            >
              {discardMutation.isPending ? 'در حال حذف...' : 'حذف کن'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
